import {useRef, useMemo, useEffect} from 'react';
import fetch from '@packages/utilities/fetch';
import {useQuery, type UseQueryResult} from '@tanstack/react-query';
import {queryClient as cmsQueryClient} from '@packages/utilities/react-query';
import {fromJS} from 'immutable';
import type {CollectionRecordType} from '@packages/systems/dynamo/records/CollectionRecord';
import {isProductsCollection} from '@packages/systems/commerce/utils/CommerceCollectionUtils';
import {
  CMSItemsQueryOptions,
  GetCollectionItemResponse,
  GetCollectionItemResponseAsJs,
  ItemAsJs,
} from '@packages/systems/cms/types';
import {unwrap} from '../helpers';
import {getCmsPreviewProjectParams} from '../utils/getCmsPreviewProjectParams';
import {getCMSBranchId} from '../utils/activeCMSBranchId';
import {useKeepPreviousCMSBranchData} from './useKeepPreviousCMSBranchData';
import {DEFAULT_SKU_SLUG} from '@packages/systems/commerce/constants';
import {ItemRecordType} from '@packages/systems/core/records/ItemRecord';
import {Variant} from '@packages/systems/commerce/core';
import {isEqual} from '@packages/utilities/memo';
import {receiveRawItems} from '@packages/systems/cms/CollectionStore';
import {reportBug} from '@packages/utilities/reporter';
import {isMintId} from '@packages/systems/core/utils/RecordUtils';
import type {ItemId} from '@packages/systems/core/records/CollectionStateType';
import {selectSiteShortNameFromSharedStores} from '@packages/systems/cms/client-selectors';
import {
  selectAllLocales,
  useLocalizationSelector,
} from '@packages/systems/localization';
import {
  useDesignerSelector,
  useDispatch,
} from '@packages/systems/designer/flux';

const STALE_TIME = 5 * 60 * 1000;

const fetchCollectionItem = ({
  collection = null,
  itemId = null,
  siteShortName = '',
  fields = '',
  format = undefined,
  locales,
  cmsBranchId = getCMSBranchId(),
}: {
  collection: CollectionRecordType | null;
  itemId: string | null;
  siteShortName: string | null;
  format?: CMSItemsQueryOptions['format'];
  fields?: CMSItemsQueryOptions['fields'];
  locales?: CMSItemsQueryOptions['locales'];
  // A query passes the branch its key was built with, so a retry or refetch
  // after a switch still reads the branch it is cached under.
  cmsBranchId?: string | null;
}): Promise<GetCollectionItemResponse> => {
  if (!collection || !siteShortName) {
    return Promise.reject();
  }

  const collectionId = collection._id ?? '';

  if (!collectionId) {
    return Promise.reject();
  }

  const params: {
    [key: string]: any;
  } = {
    target: 'staging',
  };

  if (format) {
    params.format = format;
  }

  const useCommerceProductsEndpoint =
    isProductsCollection(collection) &&
    // The /commerce/products API doesn't support formatting itemRefs
    format !== 'itemRef';

  const {previewId, projectId} = getCmsPreviewProjectParams();

  if (previewId) {
    params.preview = previewId;
  }

  if (projectId) {
    params.project = projectId;
  }

  // Only the CMS items API reads CMS branches. Ecommerce products always read main.
  if (cmsBranchId && !useCommerceProductsEndpoint && !projectId) {
    params.cmsBranchId = cmsBranchId;
  }

  let queryParams = new URLSearchParams(params).toString();

  if (fields) {
    queryParams += `&${fields}`;
  }

  if (Array.isArray(locales) && locales.length > 0) {
    queryParams += locales
      .map((locale) => (locale.cmsId ? `&locales[]=${locale.cmsId}` : ''))
      .join('');
  }

  const endpoint = (() => {
    // Note: this should only not be truthy when passing in the item
    // slug rather than the item id.
    const itemIdPortion = itemId ? `/${itemId}` : '';
    if (useCommerceProductsEndpoint) {
      return `/api/sites/${siteShortName}/commerce/products${itemIdPortion}?${queryParams}`;
    }
    if (projectId) {
      return `/comment/api/v1/collections/${collectionId}/items${itemIdPortion}?${queryParams}`;
    }

    return `/api/v1/collections/${collectionId}/items${itemIdPortion}?${queryParams}`;
  })();

  return fetch(endpoint)
    .then(unwrap)
    .then(({url, object, items}: GetCollectionItemResponseAsJs) => {
      let rawItem = items[0];
      let skus: Variant[] = [];

      // Ecommerce only exists in the first item,
      // so if the first item is in the shape of what's returned from
      // the ecommerce endpoint then we'll only want to update this one item.
      if (
        useCommerceProductsEndpoint &&
        'product' in items[0] &&
        'skus' in items[0]
      ) {
        const defaultSkuID = items[0].product[DEFAULT_SKU_SLUG];
        const defaultSku = items[0].skus.find(
          (sku: {_id: string}) => sku._id === defaultSkuID
        );
        if (defaultSku) {
          // we're safe to mutate here and we choose to do it to skip a new object creation
          items[0].product.price = defaultSku.price;
        }

        rawItem = items[0].product;
        skus = items[0].skus;
      }

      const rawItemsFromOtherLocales = Array<ItemAsJs>();
      // If we're fetching from other locales, we'll need to return those as well
      if (locales && locales.length > 1) {
        for (let i = 1; i < locales.length; i++) {
          if (i >= items.length) {
            break;
          }
          const item = items[i];
          if (!item) {
            continue;
          }
          rawItemsFromOtherLocales.push(item);
        }
      }

      const itemsToReturn = rawItem
        ? [rawItem, ...skus, ...rawItemsFromOtherLocales]
        : [...skus, ...rawItemsFromOtherLocales];

      return {
        url,
        object,
        items: itemsToReturn.map((item) => fromJS(item)) as ItemRecordType[],
      };
    });
};

const getCollectionItemQueryKey = ({
  collectionId,
  itemId,
  format,
  fields,
  locales,
  cmsBranchId,
}: {
  collectionId: string;
  itemId: string;
  format?: CMSItemsQueryOptions['format'];
  fields?: CMSItemsQueryOptions['fields'];
  locales?: CMSItemsQueryOptions['locales'];
  cmsBranchId: string | null;
}) => {
  const {previewId} = getCmsPreviewProjectParams();

  return [
    'collection',
    collectionId,
    'items',
    itemId,
    {
      format,
      fields,
      locales: locales?.map((locale) => locale.cmsId) ?? [],
      previewId,
      cmsBranchId,
    },
  ];
};

type Dispatch = (action: {type: string; [key: string]: any}) => void;

type GetCollectionItemProps = {
  collection?: CollectionRecordType | null;
  itemId: string | null;
  siteShortName?: string | null;
  params?: {
    format?: CMSItemsQueryOptions['format'];
    locales?: CMSItemsQueryOptions['locales'];
  };
  // Optional: when provided, the fetched items are also broadcast via
  // `receiveRawItems` to populate Flux stores. Omit when you only need
  // the bytes locally and don't want the `RAW_ITEMS_RECEIVED` fan-out.
  dispatch?: Dispatch;
  resetQuery?: boolean;
  forceRefresh?: boolean;
};

export const getCollectionItem = async ({
  collection,
  itemId,
  siteShortName = '',
  params = {format: 'itemRef', locales: undefined},
  dispatch,
  resetQuery = false,
  forceRefresh = false,
}: GetCollectionItemProps): Promise<ItemRecordType[] | undefined> => {
  if (!collection || !itemId || !siteShortName) {
    return Promise.reject();
  }

  const collectionId = collection._id ?? '';

  const {format, locales} = params || {};
  const cmsBranchId = getCMSBranchId();

  const queryKey = getCollectionItemQueryKey({
    collectionId,
    itemId,
    format,
    locales,
    cmsBranchId,
  });

  if (forceRefresh) {
    await cmsQueryClient.cancelQueries(queryKey);
  }

  return cmsQueryClient
    .fetchQuery(
      queryKey,
      () =>
        fetchCollectionItem({
          collection,
          itemId,
          siteShortName,
          format,
          locales,
          cmsBranchId,
        }),
      forceRefresh ? {staleTime: 0} : undefined
    )
    .then((response) => {
      return response.items.length > 0 ? response.items : null;
    })
    .catch(() => {
      // Failing silently here, since we'll report it if it wasn't found
      return null;
    })
    .then((items) => {
      if (!items) {
        reportBug(
          'ItemNotFoundWarning',
          "Attempted to find a fetched item but wasn't found.",
          {
            team: 'dynamic-data',
            priority: 'warning',
            ticket: 'https://webflow.atlassian.net/browse/DYN-2920',
            collectionId,
            itemId,
          }
        );
        return;
      }

      if (typeof dispatch === 'function') {
        const mergeExistingItemsTogether =
          format === 'itemRef' || format === 'withoutHeavyFields';

        dispatch(receiveRawItems({items, mergeExistingItemsTogether}));

        // This is used to completely reset this query after successfully saving via on-canvas editing.
        // This is to ensure that the next time we fetch the item, we get the latest data. We were
        // having problems with invalidating queries using outdated data from `revertState`.
        if (resetQuery) cmsQueryClient.resetQueries(queryKey);
      }

      return items;
    });
};

const getCollectionItemBySlugQueryKey = ({
  collectionId,
  format,
  fields,
  locales,
  cmsBranchId,
}: {
  collectionId: string;
  format?: CMSItemsQueryOptions['format'];
  fields?: CMSItemsQueryOptions['fields'];
  locales?: CMSItemsQueryOptions['locales'];
  cmsBranchId: string | null;
}) => {
  const {previewId} = getCmsPreviewProjectParams();

  return [
    'collection',
    collectionId,
    'items',
    {
      format,
      fields,
      locales: locales?.map((locale) => locale.cmsId) ?? [],
      previewId,
      cmsBranchId,
    },
  ];
};

export const getCollectionItemBySlug = ({
  collection,
  slug,
  siteShortName = '',
  params = {format: 'itemRef', locales: undefined},
  dispatch,
}: Omit<GetCollectionItemProps, 'itemId'> & {slug: string}): Promise<
  ItemRecordType | undefined
> => {
  if (!collection || !slug || !siteShortName) {
    return Promise.reject();
  }

  const collectionId = collection._id ?? '';

  const {format, locales} = params || {};

  const fields = `fields[slug][in]=${slug}`;
  const cmsBranchId = getCMSBranchId();

  return cmsQueryClient
    .fetchQuery(
      getCollectionItemBySlugQueryKey({
        collectionId,
        format,
        fields,
        locales,
        cmsBranchId,
      }),
      () =>
        fetchCollectionItem({
          collection,
          itemId: null,
          siteShortName,
          format,
          fields,
          locales,
          cmsBranchId,
        })
    )
    .then((response) => {
      return response.items.length > 0 ? response.items : null;
    })
    .catch(() => {
      // Failing silently here, since we'll report it if it wasn't found
      return null;
    })
    .then((items) => {
      if (!items) {
        reportBug(
          'ItemNotFoundWarning',
          "Attempted to find a fetched item by slug but wasn't found.",
          {
            team: 'dynamic-data',
            priority: 'warning',
            ticket: 'https://webflow.atlassian.net/browse/DYN-2920',
            collectionId,
            slug,
          }
        );
        return;
      }

      if (typeof dispatch === 'function') {
        const mergeExistingItemsTogether =
          format === 'itemRef' || format === 'withoutHeavyFields';

        dispatch(receiveRawItems({items, mergeExistingItemsTogether}));
      }

      return items[0];
    });
};

const usePrevious = <T>(value: T) => {
  const ref = useRef<T | undefined>(undefined);
  useEffect(() => {
    ref.current = value;
  });
  return ref.current;
};

export function useCollectionItem(
  collection: CollectionRecordType | null = null,
  item: ItemRecordType | ItemId | null = null,
  {
    format,
    enabled = true,
    locales,
  }: {
    format?: CMSItemsQueryOptions['format'];
    enabled?: CMSItemsQueryOptions['enabled'];
    locales?: CMSItemsQueryOptions['locales'];
  } = {}
): UseQueryResult<GetCollectionItemResponse> {
  const dispatch = useDispatch();
  const itemId = typeof item === 'string' ? item : (item?.get('_id') ?? '');
  const collectionId = collection?._id ?? '';
  const siteShortName = useDesignerSelector(
    selectSiteShortNameFromSharedStores
  );

  const allLocales = useLocalizationSelector(selectAllLocales);

  const itemsQueryParams = {
    format,
    locales: locales ?? allLocales,
  };

  let isNotAMintId = false;
  try {
    isNotAMintId = !isMintId(itemId);
  } catch (error) {
    reportBug('InvalidItemId', 'Error validating item ID format', {
      error,
      itemId,
      collectionId,
      context: 'useCollectionItem',
      siteSlug: siteShortName,
      team: 'team-build-content',
      ticket: 'https://webflow.atlassian.net/browse/CMSAUTH-3512',
    });
  }

  const {cmsBranchId, keepPreviousData, track} = useKeepPreviousCMSBranchData();
  const itemsQuery = useQuery<GetCollectionItemResponse>({
    queryKey: getCollectionItemQueryKey({
      ...itemsQueryParams,
      itemId,
      collectionId,
      cmsBranchId,
    }),
    queryFn: () =>
      fetchCollectionItem({
        ...itemsQueryParams,
        collection,
        itemId,
        siteShortName,
        cmsBranchId,
      }),
    enabled:
      enabled && Boolean(itemId) && isNotAMintId && Boolean(collectionId),
    keepPreviousData,
    staleTime: STALE_TIME,
  });
  track([itemsQuery]);

  const {status, data, fetchStatus} = itemsQuery;
  const items = useMemo(() => (data ? data.items : []), [data]);
  const previousItems = usePrevious(items);
  const previousFetchStatus = usePrevious(fetchStatus);

  // If the items changed but the status didn't, it means that the items weren't fetched, which means it was
  // an autosave update (which uses setQueriesData). In that case, we want to tell the CurrentCollectionStore not to
  // update the backupItem when receiveRawItems is dispatched.
  const wasDMAutosave =
    fetchStatus === previousFetchStatus && format === 'allFields';

  useEffect(() => {
    if (status !== 'success' || !items.length) {
      return;
    }

    const mergeExistingItemsTogether =
      format === 'itemRef' || format === 'withoutHeavyFields';

    /**
        React-query gives us a referentially instable value here, but they could be deep equal
        so we have to do that logic ourselves here because react-query doesn't do it for us
       */
    if (isEqual(previousItems, items) && !mergeExistingItemsTogether) {
      return;
    }

    // NOTE: this dispatch is to sync the items from fetching in react-query to being readable in
    // a flux context
    if (items.length > 0) {
      dispatch(
        receiveRawItems({
          items,
          mergeExistingItemsTogether,
          wasDMAutosave,
        })
      );
    }
  }, [status, items, dispatch, format, previousItems, wasDMAutosave]);

  return itemsQuery;
}
